import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PlatformLayout } from "./platform-layout";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { VoiceToTextTextarea, VoiceToTextButton } from "@/components/voice-field";
import { RichTextEditor, type RichTextEditorHandle } from "@/components/rich-text-editor";
import { cn } from "@/lib/utils";
import {
  Mail,
  Send,
  FileText,
  Droplets,
  MessageSquare,
  Ban,
  Plus,
  Trash2,
  Pencil,
  MoreVertical,
  Search,
  Loader2,
  Users,
  Play,
  Pause,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  BarChart3,
  RefreshCw,
  UserX,
  RotateCcw,
  Bot,
  ChevronDown,
  Sparkles,
  Save,
  X,
  Flame,
  Snowflake,
  Eye,
  MousePointerClick,
  UserMinus,
} from "lucide-react";

// ─── Types ─────────────────────────────────────────────────────────────────────

type Sender = { id: string; name: string; email: string };
type PlatformProspect = {
  id: string; name: string; email: string; phone?: string | null;
  company?: string | null; notes?: string | null; status: string;
  isSuppressed: boolean; suppressedAt?: string | null; suppressedReason?: string | null;
  source: string; createdAt: string;
};
type PlatformEmailTemplate = {
  id: string; name: string; subject: string; body: string; scope: string;
  isActive: boolean; createdAt: string; updatedAt: string;
};
type PlatformCampaign = {
  id: string; name: string; subject: string; body: string;
  audienceType: string; status: string; fromPlatformUserId?: string | null;
  sentCount: number; failedCount: number; suppressedCount: number;
  createdAt: string;
};
type PlatformDripCampaign = {
  id: string; name: string; status: string; audienceType: string;
  autoEnrollNewProspects: boolean; autoEnrollDelayMinutes: number;
  aiAgentId?: string | null; aiAgentName?: string | null;
  routingTier: string; exitOnReply: boolean; exitOnBooked: boolean;
  sendWindowStart?: string | null; sendWindowEnd?: string | null;
  maxSendsPerDay?: number | null;
  sentToday?: number;
  createdAt: string;
};
type PlatformDripStep = {
  id: string; campaignId: string; stepOrder: number; delayMinutes: number;
  subjectTemplate: string; bodyTemplate: string; pauseOnReply: boolean;
  sendAtTime?: string | null;
};
type ScheduledEmailRow = {
  id: string; status: string; scheduledFor: string | null;
  recipientEmail: string; recipientName: string | null;
  subject: string | null; sender: string; campaignName: string;
};
type DripStepStat = { stepOrder: number; sent: number; opens: number; clicks: number };
type DripStats = {
  enrolled: number; sent: number; replied: number;
  opens: number; clicks: number; unsubscribed: number;
  stepStats: DripStepStat[];
};
type AiAgentOption = { id: string; name: string; status: string };
type PlatformSmsCampaign = {
  id: string; name: string; audienceType: string; messageTemplate: string;
  status: string; sendMode: string; scheduledFor?: string | null;
  sentCount: number; failedCount: number; createdAt: string;
};
type PlatformConfirmationTemplate = {
  id: string; clientType?: string | null; offeringTier?: string | null;
  subject: string; body: string; isActive: boolean; isDefault: boolean;
  createdAt: string; updatedAt: string;
};
type QueueStats = {
  queueDepth: number; totalSent: number; totalFailed: number;
  totalSuppressed: number; campaignCount: number;
};

// ─── Constants ─────────────────────────────────────────────────────────────────

const AUDIENCE_OPTIONS = [
  { value: "all", label: "All Contacts" },
  { value: "prospects", label: "Prospects" },
  { value: "customers", label: "Customers" },
  { value: "waitlist", label: "Waitlist" },
  { value: "tenant_admins", label: "Tenant Admins" },
];

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  sending: "bg-blue-100 text-blue-700",
  paused: "bg-amber-100 text-amber-700",
  completed: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  active: "bg-green-100 text-green-700",
  new: "bg-gray-100 text-gray-700",
  qualified: "bg-blue-100 text-blue-700",
  converted: "bg-green-100 text-green-700",
};

const OFFERING_TIERS = ["Lite", "Pro", "Business", "Enterprise"];

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function PlatformCommunicationsPage() {
  const requestedTab = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("tab") || "send"
    : "send";
  const [activeTab, setActiveTab] = useState(requestedTab);

  return (
    <PlatformLayout>
      <div className="space-y-4" data-testid="page-platform-communications">
        <div>
          <h1 className="text-2xl font-semibold">Communications</h1>
          <p className="text-sm text-muted-foreground">
            Send emails, SMS, and manage campaigns for platform-level outreach.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-8">
            <TabsTrigger value="send" data-testid="tab-send" className="flex items-center gap-1">
              <Send className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline text-xs">Mass Email</span>
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates" className="flex items-center gap-1">
              <FileText className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline text-xs">Templates</span>
            </TabsTrigger>
            <TabsTrigger value="drip" data-testid="tab-drip" className="flex items-center gap-1">
              <Droplets className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline text-xs">Drip</span>
            </TabsTrigger>
            <TabsTrigger value="scheduled" data-testid="tab-scheduled" className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline text-xs">Scheduled</span>
            </TabsTrigger>
            <TabsTrigger value="sms" data-testid="tab-sms" className="flex items-center gap-1">
              <MessageSquare className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline text-xs">SMS</span>
            </TabsTrigger>
            <TabsTrigger value="confirmations" data-testid="tab-confirmations" className="flex items-center gap-1">
              <Mail className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline text-xs">Confirms</span>
            </TabsTrigger>
            <TabsTrigger value="suppressed" data-testid="tab-suppressed" className="flex items-center gap-1">
              <Ban className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline text-xs">Suppressed</span>
            </TabsTrigger>
            <TabsTrigger value="deliverability" data-testid="tab-deliverability" className="flex items-center gap-1">
              <BarChart3 className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline text-xs">Deliver.</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="send"><MassEmailTab /></TabsContent>
          <TabsContent value="templates"><TemplatesTab /></TabsContent>
          <TabsContent value="drip"><DripTab /></TabsContent>
          <TabsContent value="scheduled"><ScheduledTab /></TabsContent>
          <TabsContent value="sms"><SmsTab /></TabsContent>
          <TabsContent value="confirmations"><ConfirmationsTab /></TabsContent>
          <TabsContent value="suppressed"><SuppressedTab /></TabsContent>
          <TabsContent value="deliverability"><DeliverabilityTab /></TabsContent>
        </Tabs>
      </div>
    </PlatformLayout>
  );
}

// ─── Mass Email Tab ────────────────────────────────────────────────────────────

function MassEmailTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const bodyEditorRef = useRef<RichTextEditorHandle>(null);
  const [audienceType, setAudienceType] = useState("prospects");
  const [fromSenderId, setFromSenderId] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const { data: senders = [] } = useQuery<Sender[]>({
    queryKey: ["/api/platform/communications/available-senders"],
  });
  const { data: campaigns = [], isLoading } = useQuery<PlatformCampaign[]>({
    queryKey: ["/api/platform/communications/campaigns"],
  });
  const { data: deferrals = [] } = useQuery<{ id: string; itemKind: string; itemId: string; nextOpenAt: string }[]>({
    queryKey: ["/api/platform/admin/office-hours/deferrals"],
    refetchInterval: 30_000,
  });
  const deferralByCampaignId = Object.fromEntries(
    deferrals
      .filter((d) => d.itemKind === "mass-email-blast")
      .map((d) => [d.itemId, d]),
  );
  const { data: gmailStatus } = useQuery<{ email: string | null }>({
    queryKey: ["/api/platform/communications/gmail-status"],
  });
  const { data: audiencePreview } = useQuery<{ count: number }>({
    queryKey: ["/api/platform/communications/audience-preview", audienceType],
    queryFn: () => fetch(`/api/platform/communications/audience-preview?type=${audienceType}`, { credentials: "include" }).then((r) => r.json()),
  });

  const createCampaignMutation = useMutation({
    mutationFn: async (data: object) => {
      const res = await apiRequest("POST", "/api/platform/communications/campaigns", data);
      const json = await res.json();
      return { status: res.status, json };
    },
    onSuccess: ({ status, json }) => {
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/campaigns"] });
      if (status === 202 && json?.deferred) {
        const reopenStr = json.nextOpenAt
          ? ` Reopens ${new Date(json.nextOpenAt).toLocaleString()}.`
          : "";
        toast({
          title: "Send deferred — platform window is closed",
          description: `The campaign has been saved and will automatically switch to sending when the platform office hours window reopens.${reopenStr}`,
        });
      } else {
        toast({ title: "Campaign created & sending" });
      }
      setSubject(""); setBody(""); setCampaignName("");
    },
    onError: () => toast({ title: "Failed to create campaign", variant: "destructive" }),
  });

  const filtered = campaigns.filter(
    (c) => !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      {!gmailStatus?.email && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">No Gmail account connected</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Connect your Gmail account to send mass emails from the platform.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>New Mass Email</CardTitle>
          <CardDescription>Send a one-time email blast to platform contacts.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Campaign Name</Label>
              <Input
                placeholder="e.g. May Launch Announcement"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                data-testid="input-campaign-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Audience</Label>
              <Select value={audienceType} onValueChange={setAudienceType}>
                <SelectTrigger data-testid="select-audience">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUDIENCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {audiencePreview && (
                <p className="text-xs text-muted-foreground">
                  ~{audiencePreview.count} contacts in this audience
                </p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Send From</Label>
            <Select value={fromSenderId} onValueChange={setFromSenderId}>
              <SelectTrigger data-testid="select-sender">
                <SelectValue placeholder="Select a sender" />
              </SelectTrigger>
              <SelectContent>
                {senders.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`option-sender-${s.id}`}>
                    {s.name} ({s.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input
              placeholder="Email subject..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="input-subject"
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Body</Label>
              <VoiceToTextButton onTranscript={(t) => bodyEditorRef.current?.insertAtCursor(t)} />
            </div>
            <RichTextEditor ref={bodyEditorRef} value={body} onChange={setBody} placeholder="Write your email..." minHeight="200px" />
          </div>

          <div className="rounded-lg bg-muted p-3 text-sm">
            <p className="font-medium mb-1.5">Available variables</p>
            <div className="grid grid-cols-2 gap-1 text-muted-foreground text-xs">
              <span><code className="bg-background px-1 rounded">{"{{contact_name}}"}</code> – Recipient name</span>
              <span><code className="bg-background px-1 rounded">{"{{company}}"}</code> – Company name</span>
              <span><code className="bg-background px-1 rounded">{"{{sender_name}}"}</code> – Sender name</span>
              <span><code className="bg-background px-1 rounded">{"{{tier}}"}</code> – Plan tier</span>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              onClick={() =>
                createCampaignMutation.mutate({
                  name: campaignName || `Campaign ${new Date().toLocaleDateString()}`,
                  subject,
                  body,
                  audienceType,
                  fromPlatformUserId: fromSenderId || undefined,
                  status: "draft",
                })
              }
              variant="outline"
              disabled={!subject || !body || createCampaignMutation.isPending}
              data-testid="button-save-draft"
            >
              Save Draft
            </Button>
            <Button
              onClick={() =>
                createCampaignMutation.mutate({
                  name: campaignName || `Campaign ${new Date().toLocaleDateString()}`,
                  subject,
                  body,
                  audienceType,
                  fromPlatformUserId: fromSenderId || undefined,
                  status: "sending",
                })
              }
              disabled={!subject || !body || createCampaignMutation.isPending}
              data-testid="button-send"
            >
              {createCampaignMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Send className="w-4 h-4 mr-2" />
              Send Now
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Campaigns</h3>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 w-48"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            <Send className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No campaigns yet</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((c) => (
              <CampaignRow key={c.id} campaign={c} senders={senders} deferral={deferralByCampaignId[c.id]} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CampaignRow({
  campaign,
  senders,
  deferral,
}: {
  campaign: PlatformCampaign;
  senders: Sender[];
  deferral?: { nextOpenAt: string };
}) {
  const sender = senders.find((s) => s.id === campaign.fromPlatformUserId);
  const retryAt = deferral ? new Date(deferral.nextOpenAt) : null;
  return (
    <Card className="p-4" data-testid={`card-campaign-${campaign.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium truncate">{campaign.name}</p>
            <Badge className={cn("text-xs", STATUS_COLORS[campaign.status] ?? "")}>
              {campaign.status}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {AUDIENCE_OPTIONS.find((a) => a.value === campaign.audienceType)?.label ?? campaign.audienceType}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground truncate mt-0.5">{campaign.subject}</p>
          <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" />{campaign.sentCount} sent</span>
            <span className="flex items-center gap-1"><XCircle className="w-3 h-3 text-red-400" />{campaign.failedCount} failed</span>
            <span className="flex items-center gap-1"><Ban className="w-3 h-3 text-gray-400" />{campaign.suppressedCount} suppressed</span>
            {sender && <span>From: {sender.name}</span>}
          </div>
          {retryAt && (
            <div
              className="mt-2 flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400"
              data-testid={`text-retry-at-${campaign.id}`}
            >
              <Clock className="w-3 h-3 shrink-0" />
              Will auto-retry at{" "}
              <span className="font-medium">
                {retryAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>{" "}
              ({retryAt.toLocaleDateString()})
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground shrink-0">
          {new Date(campaign.createdAt).toLocaleDateString()}
        </p>
      </div>
    </Card>
  );
}

// ─── Templates Tab ─────────────────────────────────────────────────────────────

function TemplatesTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PlatformEmailTemplate | null>(null);
  const [formName, setFormName] = useState("");
  const [formSubject, setFormSubject] = useState("");
  const [formBody, setFormBody] = useState("");
  const formBodyEditorRef = useRef<RichTextEditorHandle>(null);

  const { data: templates = [], isLoading } = useQuery<PlatformEmailTemplate[]>({
    queryKey: ["/api/platform/communications/templates"],
  });

  const createMutation = useMutation({
    mutationFn: (data: object) => apiRequest("POST", "/api/platform/communications/templates", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/templates"] });
      toast({ title: "Template saved" });
      setIsDialogOpen(false);
    },
    onError: () => toast({ title: "Failed to save template", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: object }) =>
      apiRequest("PATCH", `/api/platform/communications/templates/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/templates"] });
      toast({ title: "Template updated" });
      setIsDialogOpen(false);
    },
    onError: () => toast({ title: "Failed to update template", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/platform/communications/templates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/templates"] });
      toast({ title: "Template deleted" });
    },
    onError: () => toast({ title: "Failed to delete template", variant: "destructive" }),
  });

  function openNew() {
    setEditingTemplate(null);
    setFormName(""); setFormSubject(""); setFormBody("");
    setIsDialogOpen(true);
  }

  function openEdit(t: PlatformEmailTemplate) {
    setEditingTemplate(t);
    setFormName(t.name); setFormSubject(t.subject); setFormBody(t.body);
    setIsDialogOpen(true);
  }

  function handleSave() {
    const payload = { name: formName, subject: formSubject, body: formBody };
    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const filtered = templates.filter(
    (t) => !searchQuery ||
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.subject.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 w-56"
          />
        </div>
        <Button onClick={openNew} data-testid="button-new-template">
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>No templates yet</p>
          <Button variant="outline" className="mt-3" onClick={openNew}>
            <Plus className="w-4 h-4 mr-2" /> Create Template
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((t) => (
            <Card key={t.id} className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base leading-tight">{t.name}</CardTitle>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" data-testid={`menu-template-${t.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(t)}>
                        <Pencil className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => deleteMutation.mutate(t.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="flex-1">
                <p className="text-sm font-medium text-muted-foreground truncate">{t.subject}</p>
                <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                  {t.body.replace(/<[^>]+>/g, " ").trim()}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? "Edit Template" : "New Template"}</DialogTitle>
            <DialogDescription>Platform email template for outreach campaigns.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Template Name</Label>
              <Input
                placeholder="e.g. Welcome Email"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                data-testid="input-template-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Subject</Label>
              <Input
                placeholder="Email subject..."
                value={formSubject}
                onChange={(e) => setFormSubject(e.target.value)}
                data-testid="input-template-subject"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Body</Label>
                <VoiceToTextButton onTranscript={(t) => formBodyEditorRef.current?.insertAtCursor(t)} />
              </div>
              <RichTextEditor ref={formBodyEditorRef} value={formBody} onChange={setFormBody} placeholder="Email body..." minHeight="250px" />
            </div>
            <div className="rounded-lg bg-muted p-3 text-xs">
              <p className="font-medium mb-1">Variables</p>
              <div className="flex flex-wrap gap-2 text-muted-foreground">
                {["{{contact_name}}", "{{company}}", "{{sender_name}}", "{{tier}}"].map((v) => (
                  <code key={v} className="bg-background px-1 rounded">{v}</code>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={!formName || !formSubject || !formBody || createMutation.isPending || updateMutation.isPending}
              data-testid="button-save-template"
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Drip Campaign Tab ─────────────────────────────────────────────────────────

const DRIP_AUDIENCE_OPTIONS = [
  { value: "prospects", label: "Prospects" },
  { value: "customers", label: "Customers" },
  { value: "waitlist", label: "Waitlist" },
  { value: "tenant_admins", label: "Tenant Admins" },
  { value: "all", label: "All Contacts" },
  { value: "ai_agent", label: "AI Agent" },
];

const MERGE_VARS = [
  { key: "{{firstName}}", desc: "Contact first name" },
  { key: "{{lastName}}", desc: "Contact last name" },
  { key: "{{companyName}}", desc: "Company name" },
  { key: "{{region}}", desc: "City / region" },
  { key: "{{senderName}}", desc: "Sender name" },
  { key: "{{unsubscribeUrl}}", desc: "Unsubscribe link" },
];

function DripTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<PlatformDripCampaign | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Dialog form state
  const [formName, setFormName] = useState("");
  const [formAudience, setFormAudience] = useState("prospects");
  const [formAgentId, setFormAgentId] = useState("");
  const [formRoutingTier, setFormRoutingTier] = useState<"warm" | "cold">("cold");
  const [formExitOnReply, setFormExitOnReply] = useState(true);
  const [formExitOnBooked, setFormExitOnBooked] = useState(false);
  const [formSendWindowStart, setFormSendWindowStart] = useState("");
  const [formSendWindowEnd, setFormSendWindowEnd] = useState("");
  const [formMaxSendsPerDay, setFormMaxSendsPerDay] = useState<number | null>(null);

  const { data: campaigns = [], isLoading } = useQuery<PlatformDripCampaign[]>({
    queryKey: ["/api/platform/communications/drip"],
  });

  const { data: agents = [] } = useQuery<AiAgentOption[]>({
    queryKey: ["/api/platform/ai-agents"],
    select: (data: any[]) => data.filter((a) => a.status === "active"),
  });

  const createMutation = useMutation({
    mutationFn: (data: object) => apiRequest("POST", "/api/platform/communications/drip", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/drip"] });
      toast({ title: "Drip campaign created" });
      setIsDialogOpen(false);
    },
    onError: () => toast({ title: "Failed to create drip campaign", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: object }) =>
      apiRequest("PATCH", `/api/platform/communications/drip/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/drip"] });
      toast({ title: "Drip campaign updated" });
      setIsDialogOpen(false);
    },
    onError: () => toast({ title: "Failed to update", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/platform/communications/drip/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/drip"] });
      toast({ title: "Drip campaign deleted" });
    },
  });

  function openNew() {
    setEditingCampaign(null);
    setFormName(""); setFormAudience("prospects"); setFormAgentId("");
    setFormRoutingTier("cold"); setFormExitOnReply(true); setFormExitOnBooked(false);
    setFormSendWindowStart(""); setFormSendWindowEnd(""); setFormMaxSendsPerDay(null);
    setIsDialogOpen(true);
  }

  function openEdit(c: PlatformDripCampaign) {
    setEditingCampaign(c);
    setFormName(c.name);
    // Detect AI Agent audience: if aiAgentId is set, show the "ai_agent" selection
    // even if audienceType is stored as something else — this is the canonical signal.
    setFormAudience(c.aiAgentId ? "ai_agent" : c.audienceType);
    setFormAgentId(c.aiAgentId ?? "");
    setFormRoutingTier((c.routingTier as "warm" | "cold") ?? "cold");
    setFormExitOnReply(c.exitOnReply ?? true);
    setFormExitOnBooked(c.exitOnBooked ?? false);
    setFormSendWindowStart(c.sendWindowStart ?? "");
    setFormSendWindowEnd(c.sendWindowEnd ?? "");
    setFormMaxSendsPerDay(c.maxSendsPerDay ?? null);
    setIsDialogOpen(true);
  }

  function buildPayload() {
    const isAgentAudience = formAudience === "ai_agent";
    return {
      name: formName,
      // Keep "ai_agent" as the stored audienceType so round-trips work correctly on edit.
      audienceType: formAudience,
      aiAgentId: isAgentAudience ? (formAgentId || null) : null,
      routingTier: formRoutingTier,
      exitOnReply: formExitOnReply,
      exitOnBooked: formExitOnBooked,
      sendWindowStart: formSendWindowStart || null,
      sendWindowEnd: formSendWindowEnd || null,
      maxSendsPerDay: formMaxSendsPerDay,
    };
  }

  function handleSave() {
    const payload = buildPayload();
    if (editingCampaign) {
      updateMutation.mutate({ id: editingCampaign.id, data: payload });
    } else {
      createMutation.mutate({ ...payload, status: "draft" });
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Drip Campaigns</h2>
          <p className="text-sm text-muted-foreground">Automated multi-step email sequences for platform outreach.</p>
        </div>
        <Button onClick={openNew} data-testid="button-new-drip">
          <Plus className="w-4 h-4 mr-2" /> New Campaign
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : campaigns.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          <Droplets className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>No drip campaigns yet</p>
          <Button variant="outline" className="mt-3" onClick={openNew}>
            <Plus className="w-4 h-4 mr-2" /> Create Campaign
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <DripCampaignCard
              key={c.id}
              campaign={c}
              isExpanded={expandedId === c.id}
              onToggleExpand={() => setExpandedId(expandedId === c.id ? null : c.id)}
              onEdit={() => openEdit(c)}
              onToggleStatus={() => updateMutation.mutate({
                id: c.id,
                data: { status: c.status === "active" ? "paused" : "active" },
              })}
              onDelete={() => deleteMutation.mutate(c.id)}
            />
          ))}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCampaign ? "Edit Drip Campaign" : "New Drip Campaign"}</DialogTitle>
            <DialogDescription>Configure automated multi-step email sequence settings.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Campaign Name</Label>
              <Input
                placeholder="e.g. New Prospect Nurture"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                data-testid="input-drip-name"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Audience</Label>
              <Select value={formAudience} onValueChange={setFormAudience}>
                <SelectTrigger data-testid="select-drip-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DRIP_AUDIENCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formAudience === "ai_agent" && (
              <div className="space-y-1.5">
                <Label>AI Agent</Label>
                <Select value={formAgentId} onValueChange={setFormAgentId}>
                  <SelectTrigger data-testid="select-drip-agent"><SelectValue placeholder="Select an agent…" /></SelectTrigger>
                  <SelectContent>
                    {agents.length === 0 && (
                      <SelectItem value="_none" disabled>No active agents</SelectItem>
                    )}
                    {agents.map((a) => (
                      <SelectItem key={a.id} value={a.id} data-testid={`option-agent-${a.id}`}>
                        <div className="flex items-center gap-2">
                          <Bot className="w-3.5 h-3.5" />
                          {a.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Qualified leads from this agent's runs will be enrolled into this campaign.
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Routing</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant={formRoutingTier === "cold" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFormRoutingTier("cold")}
                  data-testid="button-routing-cold"
                >
                  <Snowflake className="w-3.5 h-3.5 mr-1.5" /> Cold
                </Button>
                <Button
                  type="button"
                  variant={formRoutingTier === "warm" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFormRoutingTier("warm")}
                  data-testid="button-routing-warm"
                >
                  <Flame className="w-3.5 h-3.5 mr-1.5" /> Warm
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Cold = Maildoso relay pool. Warm = connected Gmail inbox.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Exit rules</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    checked={formExitOnReply}
                    onCheckedChange={setFormExitOnReply}
                    data-testid="switch-exit-on-reply"
                  />
                  Exit sequence when contact replies
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    checked={formExitOnBooked}
                    onCheckedChange={setFormExitOnBooked}
                    data-testid="switch-exit-on-booked"
                  />
                  Exit sequence when contact is booked
                </label>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Send window <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={formSendWindowStart}
                  onChange={(e) => setFormSendWindowStart(e.target.value)}
                  className="w-32"
                  data-testid="input-send-window-start"
                />
                <span className="text-sm text-muted-foreground">to</span>
                <Input
                  type="time"
                  value={formSendWindowEnd}
                  onChange={(e) => setFormSendWindowEnd(e.target.value)}
                  className="w-32"
                  data-testid="input-send-window-end"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Only send during this window (platform local time). Leave blank to send any time.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Daily send cap <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  className="w-32"
                  placeholder="No cap"
                  value={formMaxSendsPerDay ?? ""}
                  onChange={(e) => setFormMaxSendsPerDay(e.target.value ? parseInt(e.target.value) || 1 : null)}
                  data-testid="input-max-sends-per-day"
                />
                <span className="text-sm text-muted-foreground">emails/day max</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Enrollments are deferred (not dropped) when the cap is reached and resume the next UTC day.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={!formName || (formAudience === "ai_agent" && !formAgentId) || isPending}
              data-testid="button-save-drip"
            >
              {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingCampaign ? "Save Changes" : "Create Campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DripCampaignCard({
  campaign,
  isExpanded,
  onToggleExpand,
  onEdit,
  onToggleStatus,
  onDelete,
}: {
  campaign: PlatformDripCampaign;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
}) {
  const { data: stats, isLoading: statsLoading } = useQuery<DripStats>({
    queryKey: ["/api/platform/communications/drip", campaign.id, "stats"],
    queryFn: () =>
      fetch(`/api/platform/communications/drip/${campaign.id}/stats`, { credentials: "include" }).then((r) => r.json()),
    staleTime: 30_000,
  });

  return (
    <Card data-testid={`card-drip-${campaign.id}`}>
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium">{campaign.name}</p>
              <Badge className={cn("text-xs", STATUS_COLORS[campaign.status] ?? "")}>{campaign.status}</Badge>
              <Badge variant="outline" className="text-xs">
                {DRIP_AUDIENCE_OPTIONS.find((a) => a.value === campaign.audienceType)?.label ?? campaign.audienceType}
              </Badge>
              {campaign.aiAgentName && (
                <Badge variant="secondary" className="text-xs gap-1">
                  <Bot className="w-3 h-3" /> {campaign.aiAgentName}
                </Badge>
              )}
              <Badge variant="outline" className="text-xs gap-1">
                {campaign.routingTier === "cold" ? <Snowflake className="w-3 h-3" /> : <Flame className="w-3 h-3" />}
                {campaign.routingTier}
              </Badge>
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground">
              {statsLoading ? (
                <>
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-3 w-16" />
                </>
              ) : stats ? (
                <>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />Enrolled {stats.enrolled}</span>
                  <span className="flex items-center gap-1"><Send className="w-3 h-3" />Sent {stats.sent}</span>
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3 text-blue-500" />Opens {stats.opens ?? 0}{stats.sent > 0 ? ` (${Math.round(((stats.opens ?? 0) / stats.sent) * 100)}%)` : ""}</span>
                  <span className="flex items-center gap-1"><MousePointerClick className="w-3 h-3 text-purple-500" />Clicks {stats.clicks ?? 0}{stats.sent > 0 ? ` (${Math.round(((stats.clicks ?? 0) / stats.sent) * 100)}%)` : ""}</span>
                  <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" />Replied {stats.replied}{stats.sent > 0 ? ` (${Math.round(((stats.replied ?? 0) / stats.sent) * 100)}%)` : ""}</span>
                  {stats.sent > 0 && (
                    <span className="flex items-center gap-1"><UserMinus className="w-3 h-3 text-orange-500" />Unsubscribed {stats.unsubscribed ?? 0} ({Math.round(((stats.unsubscribed ?? 0) / stats.sent) * 100)}%)</span>
                  )}
                </>
              ) : null}
              {campaign.sendWindowStart && campaign.sendWindowEnd && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />{campaign.sendWindowStart}–{campaign.sendWindowEnd}
                </span>
              )}
              {campaign.maxSendsPerDay != null && (
                <span
                  className="flex items-center gap-1 bg-muted text-muted-foreground rounded px-1.5 py-0.5"
                  data-testid={`badge-daily-cap-${campaign.id}`}
                >
                  Today: {campaign.sentToday ?? 0}/{campaign.maxSendsPerDay}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="sm" onClick={onToggleStatus} data-testid={`button-toggle-drip-${campaign.id}`}>
              {campaign.status === "active" ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`button-edit-drip-${campaign.id}`}>
              <Pencil className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onToggleExpand} data-testid={`button-expand-drip-${campaign.id}`}>
              <Clock className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="text-destructive" onClick={onDelete} data-testid={`button-delete-drip-${campaign.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {isExpanded && <DripStepsPanel campaignId={campaign.id} />}
      </CardContent>
    </Card>
  );
}

function DripStepsPanel({ campaignId }: { campaignId: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const [isAddingStep, setIsAddingStep] = useState(false);

  // New step form
  const [newSubject, setNewSubject] = useState("");
  const [newBody, setNewBody] = useState("");
  const [newDelay, setNewDelay] = useState("1440");
  const [newPauseOnReply, setNewPauseOnReply] = useState(true);
  const [newSendAtTime, setNewSendAtTime] = useState("");

  // AI draft state
  const [draftBrief, setDraftBrief] = useState("");
  const [showDraftPanel, setShowDraftPanel] = useState(false);
  const [showMergeVars, setShowMergeVars] = useState(false);

  const { data: steps = [], isLoading } = useQuery<PlatformDripStep[]>({
    queryKey: ["/api/platform/communications/drip", campaignId, "steps"],
    queryFn: () =>
      fetch(`/api/platform/communications/drip/${campaignId}/steps`, { credentials: "include" }).then((r) => r.json()),
  });

  // Reuse the stats already fetched by DripCampaignCard (same key → cache hit, no extra network request)
  const { data: stats } = useQuery<DripStats>({
    queryKey: ["/api/platform/communications/drip", campaignId, "stats"],
    queryFn: () =>
      fetch(`/api/platform/communications/drip/${campaignId}/stats`, { credentials: "include" }).then((r) => r.json()),
    staleTime: 30_000,
  });

  const addStepMutation = useMutation({
    mutationFn: (data: object) =>
      apiRequest("POST", `/api/platform/communications/drip/${campaignId}/steps`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/drip", campaignId, "steps"] });
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/drip", campaignId, "stats"] });
      toast({ title: "Step added" });
      setIsAddingStep(false);
      setNewSubject(""); setNewBody(""); setNewDelay("1440"); setNewPauseOnReply(true); setNewSendAtTime("");
      setDraftBrief(""); setShowDraftPanel(false);
    },
    onError: () => toast({ title: "Failed to add step", variant: "destructive" }),
  });

  const deleteStepMutation = useMutation({
    mutationFn: (stepId: string) =>
      apiRequest("DELETE", `/api/platform/communications/drip/${campaignId}/steps/${stepId}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/drip", campaignId, "steps"] }),
  });

  const aiDraftMutation = useMutation({
    mutationFn: async (brief: string) => {
      const res = await apiRequest("POST", "/api/platform/communications/drip/ai-draft", { brief });
      return res.json() as Promise<{ subject: string; body: string }>;
    },
    onSuccess: (data) => {
      setNewSubject(data.subject);
      setNewBody(data.body);
      setShowDraftPanel(false);
      toast({ title: "AI draft applied" });
    },
    onError: () => toast({ title: "AI draft failed", variant: "destructive" }),
  });

  if (isLoading) return <div className="pt-3"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  return (
    <div className="mt-4 border-t pt-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Steps ({steps.length})</p>
        <Button variant="outline" size="sm" onClick={() => setIsAddingStep(true)} disabled={isAddingStep} data-testid="button-add-step">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Step
        </Button>
      </div>

      {steps.length === 0 && !isAddingStep && (
        <p className="text-sm text-muted-foreground">No steps yet. Add the first step to start the sequence.</p>
      )}

      {steps.map((step, idx) => (
        <StepRow
          key={step.id}
          step={step}
          index={idx}
          campaignId={campaignId}
          isEditing={editingStepId === step.id}
          onEditStart={() => setEditingStepId(step.id)}
          onEditEnd={() => setEditingStepId(null)}
          onDelete={() => deleteStepMutation.mutate(step.id)}
          stepStat={stats?.stepStats?.find((s) => s.stepOrder === step.stepOrder)}
        />
      ))}

      {isAddingStep && (
        <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">Step {steps.length + 1}</p>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDraftPanel(!showDraftPanel)}
                data-testid="button-ai-draft"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5 text-purple-500" />
                AI Draft
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                setIsAddingStep(false);
                setNewSubject(""); setNewBody(""); setShowDraftPanel(false);
              }}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {showDraftPanel && (
            <div className="border rounded-md p-3 bg-purple-50 dark:bg-purple-950/30 space-y-2">
              <p className="text-xs font-medium text-purple-700 dark:text-purple-300">AI Draft — describe what this step should accomplish</p>
              <Textarea
                placeholder="e.g. Follow-up to a venue owner after no reply, mention our booking platform saves 5h/week on scheduling…"
                value={draftBrief}
                onChange={(e) => setDraftBrief(e.target.value)}
                rows={3}
                className="text-sm"
                data-testid="input-ai-brief"
              />
              <Button
                size="sm"
                onClick={() => aiDraftMutation.mutate(draftBrief)}
                disabled={!draftBrief.trim() || aiDraftMutation.isPending}
              >
                {aiDraftMutation.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                Generate
              </Button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Delay before sending</Label>
              <Select value={newDelay} onValueChange={setNewDelay}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="1440">1 day</SelectItem>
                  <SelectItem value="2880">2 days</SelectItem>
                  <SelectItem value="5040">3.5 days</SelectItem>
                  <SelectItem value="10080">1 week</SelectItem>
                  <SelectItem value="20160">2 weeks</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Send at time (CT)
                <span className="text-muted-foreground font-normal ml-1">optional</span>
              </Label>
              <Input
                type="time"
                value={newSendAtTime}
                onChange={(e) => setNewSendAtTime(e.target.value)}
                placeholder="HH:MM"
                data-testid="input-step-send-at-time"
                className="text-sm"
              />
              <p className="text-xs text-muted-foreground">Bypasses send-window gate</p>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              placeholder="Step subject…"
              data-testid="input-step-subject"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Body</Label>
            <Textarea
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              placeholder="Step body…"
              rows={5}
              data-testid="input-step-body"
            />
          </div>

          <Collapsible open={showMergeVars} onOpenChange={setShowMergeVars}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="text-xs h-7 gap-1">
                <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", showMergeVars && "rotate-180")} />
                Available variables
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="rounded-md bg-muted p-3 mt-1 grid grid-cols-2 gap-1 text-xs">
                {MERGE_VARS.map((v) => (
                  <div key={v.key} className="flex items-center gap-1.5">
                    <code className="bg-background px-1 rounded text-primary">{v.key}</code>
                    <span className="text-muted-foreground">{v.desc}</span>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch checked={newPauseOnReply} onCheckedChange={setNewPauseOnReply} />
            Pause sequence on reply
          </label>

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setIsAddingStep(false); setNewSubject(""); setNewBody(""); setNewSendAtTime(""); }}>Cancel</Button>
            <Button
              size="sm"
              onClick={() =>
                addStepMutation.mutate({
                  stepOrder: steps.length,
                  delayMinutes: parseInt(newDelay, 10),
                  subjectTemplate: newSubject,
                  bodyTemplate: newBody,
                  pauseOnReply: newPauseOnReply,
                  sendAtTime: newSendAtTime || null,
                })
              }
              disabled={!newSubject || !newBody || addStepMutation.isPending}
              data-testid="button-add-step-save"
            >
              {addStepMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Add Step
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepRow({
  step,
  index,
  campaignId,
  isEditing,
  onEditStart,
  onEditEnd,
  onDelete,
  stepStat,
}: {
  step: PlatformDripStep;
  index: number;
  campaignId: string;
  isEditing: boolean;
  onEditStart: () => void;
  onEditEnd: () => void;
  onDelete: () => void;
  stepStat?: DripStepStat;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [subject, setSubject] = useState(step.subjectTemplate);
  const [body, setBody] = useState(step.bodyTemplate);
  const [delay, setDelay] = useState(String(step.delayMinutes));
  const [pauseOnReply, setPauseOnReply] = useState(step.pauseOnReply);
  const [sendAtTime, setSendAtTime] = useState(step.sendAtTime ?? "");
  const [showMergeVars, setShowMergeVars] = useState(false);

  // AI Draft state for existing step edit
  const [showDraftPanel, setShowDraftPanel] = useState(false);
  const [draftBrief, setDraftBrief] = useState("");

  const patchMutation = useMutation({
    mutationFn: (data: object) =>
      apiRequest("PATCH", `/api/platform/communications/drip/${campaignId}/steps/${step.id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/drip", campaignId, "steps"] });
      toast({ title: "Step saved" });
      onEditEnd();
    },
    onError: () => toast({ title: "Failed to save step", variant: "destructive" }),
  });

  const aiDraftMutation = useMutation({
    mutationFn: async (brief: string) => {
      const res = await apiRequest("POST", "/api/platform/communications/drip/ai-draft", { brief });
      return res.json() as Promise<{ subject: string; body: string }>;
    },
    onSuccess: (data) => {
      setSubject(data.subject);
      setBody(data.body);
      setShowDraftPanel(false);
      toast({ title: "AI draft applied" });
    },
    onError: () => toast({ title: "AI draft failed", variant: "destructive" }),
  });

  if (!isEditing) {
    return (
      <div className="flex items-start gap-3 bg-muted rounded-lg p-3">
        <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs shrink-0 mt-0.5">
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{step.subjectTemplate}</p>
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-xs text-muted-foreground">
              {step.delayMinutes < 60
                ? `${step.delayMinutes}m delay`
                : step.delayMinutes < 1440
                ? `${Math.round(step.delayMinutes / 60)}h delay`
                : `${Math.round(step.delayMinutes / 1440)}d delay`}
              {step.sendAtTime && ` · ${step.sendAtTime} CT`}
              {step.pauseOnReply && " · pauses on reply"}
            </p>
            {stepStat && (stepStat.sent > 0 || stepStat.opens > 0) && (
              <span className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`step-stats-${step.id}`}>
                <span className="flex items-center gap-0.5">
                  <Send className="w-3 h-3" />{stepStat.sent}
                </span>
                <span className="flex items-center gap-0.5 text-blue-600 dark:text-blue-400">
                  <Eye className="w-3 h-3" />{stepStat.opens}{stepStat.sent > 0 ? ` (${Math.round((stepStat.opens / stepStat.sent) * 100)}%)` : ""}
                </span>
                <span className="flex items-center gap-0.5 text-purple-600 dark:text-purple-400">
                  <MousePointerClick className="w-3 h-3" />{stepStat.clicks}{stepStat.sent > 0 ? ` (${Math.round((stepStat.clicks / stepStat.sent) * 100)}%)` : ""}
                </span>
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEditStart} data-testid={`button-edit-step-${step.id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={onDelete} data-testid={`button-delete-step-${step.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4 space-y-3 bg-background">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">Edit Step {index + 1}</p>
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDraftPanel(!showDraftPanel)}
            data-testid={`button-ai-draft-step-${step.id}`}
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5 text-purple-500" />
            AI Draft
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEditEnd}>
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {showDraftPanel && (
        <div className="border rounded-md p-3 bg-purple-50 dark:bg-purple-950/30 space-y-2">
          <p className="text-xs font-medium text-purple-700 dark:text-purple-300">Describe what this step should accomplish</p>
          <Textarea
            placeholder="e.g. Final follow-up, create urgency, mention limited availability…"
            value={draftBrief}
            onChange={(e) => setDraftBrief(e.target.value)}
            rows={2}
            className="text-sm"
          />
          <Button
            size="sm"
            onClick={() => aiDraftMutation.mutate(draftBrief)}
            disabled={!draftBrief.trim() || aiDraftMutation.isPending}
          >
            {aiDraftMutation.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
            Generate
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Delay before sending</Label>
          <Select value={delay} onValueChange={setDelay}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="60">1 hour</SelectItem>
              <SelectItem value="1440">1 day</SelectItem>
              <SelectItem value="2880">2 days</SelectItem>
              <SelectItem value="5040">3.5 days</SelectItem>
              <SelectItem value="10080">1 week</SelectItem>
              <SelectItem value="20160">2 weeks</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Send at time (CT)
            <span className="text-muted-foreground font-normal ml-1">optional</span>
          </Label>
          <Input
            type="time"
            value={sendAtTime}
            onChange={(e) => setSendAtTime(e.target.value)}
            data-testid={`input-step-send-at-time-${step.id}`}
            className="text-sm"
          />
          <p className="text-xs text-muted-foreground">Bypasses send-window gate</p>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Subject</Label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label>Body</Label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} />
      </div>

      <Collapsible open={showMergeVars} onOpenChange={setShowMergeVars}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="text-xs h-7 gap-1">
            <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", showMergeVars && "rotate-180")} />
            Available variables
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="rounded-md bg-muted p-3 mt-1 grid grid-cols-2 gap-1 text-xs">
            {MERGE_VARS.map((v) => (
              <div key={v.key} className="flex items-center gap-1.5">
                <code className="bg-background px-1 rounded text-primary">{v.key}</code>
                <span className="text-muted-foreground">{v.desc}</span>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Switch checked={pauseOnReply} onCheckedChange={setPauseOnReply} />
        Pause sequence on reply
      </label>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onEditEnd}>Cancel</Button>
        <Button
          size="sm"
          onClick={() => patchMutation.mutate({
            subjectTemplate: subject,
            bodyTemplate: body,
            delayMinutes: parseInt(delay, 10),
            pauseOnReply,
            sendAtTime: sendAtTime || null,
          })}
          disabled={!subject || !body || patchMutation.isPending}
          data-testid={`button-save-step-${step.id}`}
        >
          {patchMutation.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          Save Step
        </Button>
      </div>
    </div>
  );
}

// ─── Scheduled Tab ─────────────────────────────────────────────────────────────

const SCHEDULED_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "queued", label: "Queued" },
  { value: "sent", label: "Sent" },
  { value: "failed", label: "Failed" },
  { value: "all", label: "All" },
];

const SCHEDULED_STATUS_COLORS: Record<string, string> = {
  pending: "bg-blue-100 text-blue-700",
  queued:  "bg-yellow-100 text-yellow-700",
  sent:    "bg-green-100 text-green-700",
  failed:  "bg-red-100 text-red-700",
};

const PLATFORM_TZ = "America/Chicago";

function formatScheduledFor(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: PLATFORM_TZ,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(iso));
}

type ScheduledPage = { rows: ScheduledEmailRow[]; nextCursor: string | null };

function ScheduledTab() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allRows, setAllRows] = useState<ScheduledEmailRow[]>([]);

  // Reset accumulated rows when filter changes
  useEffect(() => {
    setAllRows([]);
    setCursor(undefined);
  }, [statusFilter]);

  const { data, isLoading, isFetching, refetch } = useQuery<ScheduledPage>({
    queryKey: ["/api/platform/communications/scheduled", statusFilter, cursor],
    queryFn: () => {
      const url = `/api/platform/communications/scheduled?status=${statusFilter}&limit=50${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`;
      return fetch(url, { credentials: "include" }).then((r) => r.json());
    },
  });

  // Append new page rows to the accumulated list
  useEffect(() => {
    if (data?.rows) {
      setAllRows((prev) => {
        // Deduplicate on id
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...data.rows.filter((r) => !seen.has(r.id))];
      });
    }
  }, [data]);

  function handleRefresh() {
    setAllRows([]);
    setCursor(undefined);
    refetch();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">Scheduled Emails</h2>
          <p className="text-sm text-muted-foreground">
            Platform drip emails waiting to fire, plus historical send records. Times shown in CT.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="select-scheduled-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCHEDULED_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={handleRefresh} data-testid="button-refresh-scheduled">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {isLoading && allRows.length === 0 ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : allRows.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          <Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>No {statusFilter === "all" ? "" : statusFilter + " "}emails found</p>
        </Card>
      ) : (
        <>
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Status</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Recipient</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Subject</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Campaign</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Sender</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Scheduled For (CT)</th>
                  </tr>
                </thead>
                <tbody>
                  {allRows.map((row) => (
                    <tr key={row.id} className="border-b last:border-0 hover:bg-muted/20" data-testid={`row-scheduled-${row.id}`}>
                      <td className="px-4 py-2.5">
                        <Badge className={cn("text-xs capitalize", SCHEDULED_STATUS_COLORS[row.status] ?? "bg-muted text-muted-foreground")}>
                          {row.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5">
                        <p className="font-medium truncate max-w-[160px]">{row.recipientName ?? row.recipientEmail}</p>
                        {row.recipientName && (
                          <p className="text-xs text-muted-foreground truncate max-w-[160px]">{row.recipientEmail}</p>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <p className="truncate max-w-[200px]">
                          {row.subject ?? <span className="text-muted-foreground italic">—</span>}
                        </p>
                      </td>
                      <td className="px-4 py-2.5">
                        <p className="truncate max-w-[140px] text-muted-foreground">{row.campaignName}</p>
                      </td>
                      <td className="px-4 py-2.5">
                        <p className="text-xs text-muted-foreground">{row.sender}</p>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-sm">
                        {formatScheduledFor(row.scheduledFor)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {data?.nextCursor && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={isFetching}
                onClick={() => setCursor(data.nextCursor!)}
                data-testid="button-load-more-scheduled"
              >
                {isFetching ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Load more
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── SMS Tab ───────────────────────────────────────────────────────────────────

function SmsTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formName, setFormName] = useState("");
  const [formAudience, setFormAudience] = useState("prospects");
  const [formMessage, setFormMessage] = useState("");

  const { data: campaigns = [], isLoading } = useQuery<PlatformSmsCampaign[]>({
    queryKey: ["/api/platform/communications/sms"],
  });

  const createMutation = useMutation({
    mutationFn: (data: object) => apiRequest("POST", "/api/platform/communications/sms", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/sms"] });
      toast({ title: "SMS campaign created" });
      setIsDialogOpen(false);
    },
    onError: () => toast({ title: "Failed to create SMS campaign", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/platform/communications/sms/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/sms"] });
      toast({ title: "SMS campaign deleted" });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">SMS Campaigns</h2>
          <p className="text-sm text-muted-foreground">Send text message campaigns to platform contacts.</p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)} data-testid="button-new-sms">
          <Plus className="w-4 h-4 mr-2" /> New SMS Campaign
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : campaigns.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>No SMS campaigns yet</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <Card key={c.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{c.name}</p>
                    <Badge className={cn("text-xs", STATUS_COLORS[c.status] ?? "")}>{c.status}</Badge>
                    <Badge variant="outline" className="text-xs">
                      {AUDIENCE_OPTIONS.find((a) => a.value === c.audienceType)?.label ?? c.audienceType}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.messageTemplate}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {c.sentCount} sent · {c.failedCount} failed
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive"
                  onClick={() => deleteMutation.mutate(c.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New SMS Campaign</DialogTitle>
            <DialogDescription>Send an SMS blast to platform contacts.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Campaign Name</Label>
              <Input
                placeholder="e.g. Holiday Special"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                data-testid="input-sms-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Audience</Label>
              <Select value={formAudience} onValueChange={setFormAudience}>
                <SelectTrigger data-testid="select-sms-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AUDIENCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Message</Label>
              <VoiceToTextTextarea
                placeholder="Your SMS message... (160 chars per segment)"
                value={formMessage}
                onChange={(e) => setFormMessage(e.target.value)}
                rows={4}
                data-testid="textarea-sms-message"
              />
              <p className="text-xs text-muted-foreground text-right">{formMessage.length} chars</p>
            </div>
            <div className="rounded-lg bg-muted p-3 text-xs">
              <p className="font-medium mb-1">Variables</p>
              <div className="flex flex-wrap gap-2 text-muted-foreground">
                {["{{contact_name}}", "{{company}}"].map((v) => (
                  <code key={v} className="bg-background px-1 rounded">{v}</code>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createMutation.mutate({
                name: formName, audienceType: formAudience,
                messageTemplate: formMessage, status: "draft", sendMode: "now",
              })}
              disabled={!formName || !formMessage || createMutation.isPending}
              data-testid="button-create-sms"
            >
              {createMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Confirmations Tab ─────────────────────────────────────────────────────────

function ConfirmationsTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [clientType, setClientType] = useState("");
  const [offeringTier, setOfferingTier] = useState(OFFERING_TIERS[0]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const bodyEditorRef = useRef<RichTextEditorHandle>(null);

  const { data: businessTypes = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/platform/business-types"],
  });

  useEffect(() => {
    if (!clientType && businessTypes.length > 0) {
      setClientType(businessTypes[0].name);
    }
  }, [businessTypes, clientType]);

  const { data: templates = [], isLoading } = useQuery<PlatformConfirmationTemplate[]>({
    queryKey: ["/api/platform/communications/confirmation-templates"],
  });

  const selectedTemplate = templates.find(
    (t) => t.clientType === clientType && t.offeringTier === offeringTier,
  ) ?? templates.find((t) => t.isDefault);

  useEffect(() => {
    setSubject(selectedTemplate?.subject ?? "");
    setBody(selectedTemplate?.body ?? "");
  }, [clientType, offeringTier, templates]);

  const saveMutation = useMutation({
    mutationFn: (data: object) => {
      if (selectedTemplate && selectedTemplate.clientType === clientType && selectedTemplate.offeringTier === offeringTier) {
        return apiRequest("PATCH", `/api/platform/communications/confirmation-templates/${selectedTemplate.id}`, data);
      }
      return apiRequest("POST", "/api/platform/communications/confirmation-templates", data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/confirmation-templates"] });
      toast({ title: "Confirmation template saved" });
    },
    onError: () => toast({ title: "Failed to save template", variant: "destructive" }),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Confirmation Email Templates</h2>
        <p className="text-sm text-muted-foreground">
          Customize signup / onboarding confirmation emails by business type and plan tier.
        </p>
      </div>

      {/* Per client-type / offering-tier matrix */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Onboarding Confirmation Matrix</CardTitle>
          <CardDescription>
            Customize signup / onboarding confirmation emails by business type and plan tier.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Business Type</Label>
              <Select value={clientType} onValueChange={setClientType} disabled={businessTypes.length === 0}>
                <SelectTrigger data-testid="select-confirmation-client-type">
                  <SelectValue placeholder={businessTypes.length === 0 ? "No business types available" : "Select a business type"} />
                </SelectTrigger>
                <SelectContent>
                  {businessTypes.map((bt) => (
                    <SelectItem key={bt.id} value={bt.name}>{bt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Plan Tier</Label>
              <Select value={offeringTier} onValueChange={setOfferingTier}>
                <SelectTrigger data-testid="select-confirmation-tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OFFERING_TIERS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input
              placeholder="Subject line..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="input-confirmation-subject"
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Body</Label>
              <VoiceToTextButton onTranscript={(t) => bodyEditorRef.current?.insertAtCursor(t)} />
            </div>
            <RichTextEditor ref={bodyEditorRef} value={body} onChange={setBody} placeholder="Email content..." minHeight="300px" />
          </div>

          <div className="rounded-lg bg-muted p-3 text-xs">
            <p className="font-medium mb-1.5">Available Variables</p>
            <div className="grid grid-cols-2 gap-1 text-muted-foreground">
              {[
                ["{{contact_name}}", "Recipient name"],
                ["{{company}}", "Company / org name"],
                ["{{tier}}", "Plan tier"],
                ["{{sender_name}}", "Sender name"],
              ].map(([v, l]) => (
                <span key={v}><code className="bg-background px-1 rounded">{v}</code> – {l}</span>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between border-t pt-4">
            <p className="text-sm text-muted-foreground">
              {selectedTemplate
                ? `Last saved: ${new Date(selectedTemplate.updatedAt).toLocaleDateString()}`
                : "No template saved for this combination yet"}
            </p>
            <Button
              onClick={() =>
                saveMutation.mutate({ clientType, offeringTier, subject, body, isActive: true })
              }
              disabled={!subject || !body || saveMutation.isPending}
              data-testid="button-save-confirmation"
            >
              {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Template
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Suppressed Contacts Tab ───────────────────────────────────────────────────

function SuppressedTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");

  const { data: suppressedProspects = [], isLoading } = useQuery<PlatformProspect[]>({
    queryKey: ["/api/platform/communications/prospects", "suppressed"],
    queryFn: () =>
      fetch("/api/platform/communications/prospects?suppressed=true", { credentials: "include" }).then((r) => r.json()),
  });

  const unsuppressMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/platform/communications/prospects/${id}/unsuppress`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/communications/prospects"] });
      toast({ title: "Contact unsuppressed" });
    },
    onError: () => toast({ title: "Failed to unsuppress contact", variant: "destructive" }),
  });

  const filtered = suppressedProspects.filter(
    (p) => !searchQuery ||
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.email.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Suppressed Contacts</h2>
          <p className="text-sm text-muted-foreground">
            Contacts whose emails bounced or who opted out. Excluded from all campaigns.
          </p>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 w-48"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          <UserX className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>{searchQuery ? "No matching suppressed contacts" : "No suppressed contacts"}</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => (
            <Card key={p.id} className="p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{p.name}</p>
                  <p className="text-sm text-muted-foreground">{p.email}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Reason: {p.suppressedReason ?? "unknown"}
                    {p.suppressedAt && ` · ${new Date(p.suppressedAt).toLocaleDateString()}`}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => unsuppressMutation.mutate(p.id)}
                  disabled={unsuppressMutation.isPending}
                  data-testid={`button-unsuppress-${p.id}`}
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  Unsuppress
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Deliverability Tab ────────────────────────────────────────────────────────

function DeliverabilityTab() {
  const { data: gmailStatus } = useQuery<{ email: string | null }>({
    queryKey: ["/api/platform/communications/gmail-status"],
  });
  const { data: senders = [] } = useQuery<Sender[]>({
    queryKey: ["/api/platform/communications/available-senders"],
  });
  const { data: stats, isLoading, refetch } = useQuery<QueueStats>({
    queryKey: ["/api/platform/communications/deliverability/queue-stats"],
    refetchInterval: 30000,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Deliverability</h2>
          <p className="text-sm text-muted-foreground">Platform sending health and campaign statistics.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-refresh-stats">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Gmail Status */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Sending Accounts</CardTitle>
          <CardDescription>Platform admin Gmail accounts connected for sending.</CardDescription>
        </CardHeader>
        <CardContent>
          {senders.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 p-3 rounded-md">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              No Gmail accounts connected. Connect an account to enable email sending.
            </div>
          ) : (
            <div className="space-y-2">
              {senders.map((s) => (
                <div key={s.id} className="flex items-center gap-3 p-3 border rounded-md">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-semibold">
                    {s.name?.[0]?.toUpperCase() ?? "?"}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{s.name}</p>
                    <p className="text-xs text-muted-foreground">{s.email}</p>
                  </div>
                  <Badge className="ml-auto bg-green-100 text-green-700 text-xs">Connected</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      {isLoading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : stats ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Total Campaigns", value: stats.campaignCount, icon: Mail, color: "text-blue-600" },
              { label: "Total Sent", value: stats.totalSent, icon: CheckCircle, color: "text-green-600" },
              { label: "Total Failed", value: stats.totalFailed, icon: XCircle, color: "text-red-500" },
              { label: "Suppressed", value: stats.totalSuppressed, icon: Ban, color: "text-gray-500" },
            ].map(({ label, value, icon: Icon, color }) => (
              <Card key={label}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={cn("w-4 h-4", color)} />
                    <p className="text-xs text-muted-foreground">{label}</p>
                  </div>
                  <p className="text-2xl font-semibold">{value.toLocaleString()}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Delivery Rate</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.totalSent + stats.totalFailed === 0 ? (
                <p className="text-sm text-muted-foreground">No emails sent yet.</p>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Success rate</span>
                    <span className="font-medium">
                      {(
                        (stats.totalSent / (stats.totalSent + stats.totalFailed)) *
                        100
                      ).toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 rounded-full"
                      style={{
                        width: `${(stats.totalSent / (stats.totalSent + stats.totalFailed)) * 100}%`,
                      }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {stats.totalSent.toLocaleString()} successful out of{" "}
                    {(stats.totalSent + stats.totalFailed).toLocaleString()} attempted
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
